import React from 'react';
import { EventSnapshot, TopMatchKombiItem } from '../../../api/types';
import { getOutrightEvents, getTopMatchKombis } from '../../../api/api';
import { OddsButton } from '../../OddsButton';
import { useTranslation } from '../../../i18n';
import { getCountryFlagUrl } from '../../../utils/flagUtils';
import { translateMarketFilterLabel } from '../../../utils/marketTranslations';
import { getEventName } from '../../../utils/eventHelpers';

// Merged catalog events are not pre-sorted; order by start time so tournament
// blocks, day groups, and the matches inside them read chronologically.
function eventStartMs(ev: EventSnapshot): number {
  const t = ev.event?.scheduled_at ?? ev.event?.start_time;
  const n = t ? new Date(t).getTime() : NaN;
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}


function formatOdds(val: string | number | undefined): string {
  if (val === undefined || val === null) return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

interface CategoryMatchListProps {
  events: EventSnapshot[];
  topMarket1: string | number;
  setTopMarket1: (m: string | number) => void;
  topMarket2: string | number;
  setTopMarket2: (m: string | number) => void;
  topMarket3: string | number;
  setTopMarket3: (m: string | number) => void;
  topMarket4: string | number;
  setTopMarket4: (m: string | number) => void;
  topMarket5: string | number;
  setTopMarket5: (m: string | number) => void;
  openDropdown: string | null;
  setOpenDropdown: (val: string | null | ((prev: string | null) => string | null)) => void;
  options: (string | number)[];
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string, isUp?: boolean, isDown?: boolean) => React.ReactNode;
  handleEventClick: (ev: EventSnapshot) => void;
  formatTime: (iso: string) => string;
  categoryPrefix?: string;
  hasTabs?: boolean;
  onAddMatchKombi?: (item: TopMatchKombiItem) => void;
  onOpenBuilder?: (item: TopMatchKombiItem) => void;
  isSelected?: (item: TopMatchKombiItem) => boolean;
  hideSecondMarket?: boolean;
}

export const CategoryMatchList: React.FC<CategoryMatchListProps> = ({
  onAddMatchKombi,
  onOpenBuilder,
  isSelected,

  events,
  categoryPrefix = '',
  hasTabs = false,
  topMarket1,
  setTopMarket1,
  topMarket2,
  setTopMarket2,
  topMarket3,
  setTopMarket3,
  topMarket4,
  setTopMarket4,
  topMarket5,
  setTopMarket5,
  openDropdown,
  setOpenDropdown,
  options,
  renderMarketColHeader,
  renderMarketColCells,
  handleEventClick,
  formatTime,
  hideSecondMarket = false,
}) => {
  const { locale } = useTranslation();

  const { t } = useTranslation();


  const [activeTab, setActiveTab] = React.useState<'matches'|'matchkombi'|'outrights'|'statistics'>('matches');
  const [outrights, setOutrights] = React.useState<EventSnapshot[]>([]);
  const [matchKombis, setMatchKombis] = React.useState<TopMatchKombiItem[]>([]);
  const [loadingTab, setLoadingTab] = React.useState(false);

  React.useEffect(() => {
    const onlyOutrights = events.length > 0 && events.every(e => (e.event as any)?.scope === 'outright');
    if (onlyOutrights) setActiveTab('outrights');
    else setActiveTab('matches');
  }, [events[0]?.event?.tournament_id]);

  React.useEffect(() => {
    if (!hasTabs || !events[0]?.event?.tournament_id) return;
    const tId = events[0].event.tournament_id;
    
    if (activeTab === 'outrights') {
      setLoadingTab(true);
      getOutrightEvents({ tournament_id: tId, limit: 50 })
        .then(res => setOutrights(res.items || []))
        .catch(() => setOutrights([]))
        .finally(() => setLoadingTab(false));
    } else if (activeTab === 'matchkombi') {
      setLoadingTab(true);
      getTopMatchKombis({ tournament_id: tId, limit: 50 })
        .then(res => setMatchKombis(res.items || []))
        .catch(() => setMatchKombis([]))
        .finally(() => setLoadingTab(false));
    }
  }, [activeTab, hasTabs, events[0]?.event?.tournament_id]);

  if (events.length === 0) return null;

  // Group by tournament (chronologically ordered so day groups and matches
  // within each tournament follow start time)
  const sortedEvents = [...events].sort((a, b) => eventStartMs(a) - eventStartMs(b));
  
  const byTournament = sortedEvents.reduce((acc, ev) => {
    const t = ev.event.tournament_name;
    if (!acc[t]) acc[t] = [];
    acc[t].push(ev);
    return acc;
  }, {} as Record<string, EventSnapshot[]>);

  return (
    <div className="mb-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 relative z-10">
        
        {/* Tabs (Optional) */}
        {hasTabs && (
          <div className="flex items-center px-4 pt-2 border-b border-gray-200">
            <button onClick={() => setActiveTab('matches')} className={`flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 mx-2 ${activeTab === 'matches' ? 'border-[#cc0000] text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              <span className={`text-[18px] font-black ${activeTab === 'matches' ? 'text-[#cc0000]' : ''}`}>＋</span>
              <span className="text-[13px] font-bold mt-1">Matches</span>
            </button>
            <button onClick={() => setActiveTab('matchkombi')} className={`flex flex-col items-center justify-center min-w-[90px] pb-2 border-b-2 mx-2 ${activeTab === 'matchkombi' ? 'border-[#cc0000] text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              <span className="text-[18px] font-black italic">N<span>+</span></span>
              <span className="text-[13px] font-bold mt-1">MatchKombi+</span>
            </button>
            <button onClick={() => setActiveTab('outrights')} className={`flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 mx-2 ${activeTab === 'outrights' ? 'border-[#cc0000] text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              <span className="text-[18px]">📅</span>
              <span className="text-[13px] font-bold mt-1">Outrights</span>
            </button>
            <button onClick={() => setActiveTab('statistics')} className={`flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 mx-2 ${activeTab === 'statistics' ? 'border-[#cc0000] text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              <span className="text-[18px]">📊</span>
              <span className="text-[13px] font-bold mt-1">Statistics</span>
            </button>
          </div>
        )}

        {activeTab === 'matches' && (
        <>
        {/* Header Row */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-[56px] z-20 bg-white">
          <div className="flex items-center gap-2">
            {!hasTabs && (
              <>
                <span className="text-[18px]">⚽</span>
                <h2 className="text-[18px] font-bold text-gray-900 ml-1">{t('sport.football')}</h2>
              </>
            )}
          </div>

          {/* Same geometry as the label row below: [mx-1 w-151]×5 + w-52 spacer,
              so each dropdown sits exactly over its odds column. */}
          <div className="flex items-center">
            <div className="relative mx-1 flex-shrink-0">
              <div onClick={() => setOpenDropdown(o => o === 'c1' ? null : 'c1')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket1, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c1' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative mx-1 flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
              <div onClick={() => setOpenDropdown(o => o === 'c2' ? null : 'c2')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket2, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c2' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative mx-1 flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
              <div onClick={() => setOpenDropdown(o => o === 'c3' ? null : 'c3')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket3, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c3' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!hideSecondMarket && (
              <div className="relative mx-1 flex-shrink-0 hidden min-[1650px]:block">
                <div onClick={() => setOpenDropdown(o => o === 'c4' ? null : 'c4')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                   <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket4, locale)}</span>
                   <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {openDropdown === 'c4' && (
                  <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                    <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                    {options.map(opt => (
                      <button key={opt} onClick={() => { setTopMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                        <div className="w-4">{topMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                        <span className={topMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {!hideSecondMarket && (
                <div className="relative mx-1 flex-shrink-0 hidden min-[1750px]:block">
                  <div onClick={() => setOpenDropdown(o => o === `cat5-${categoryPrefix}` ? null : `cat5-${categoryPrefix}`)} className="w-[151px] bg-[#f0f2f5] rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                    <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket5, locale)}</span>
                    <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {openDropdown === `cat5-${categoryPrefix}` && (
                    <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                      <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Markt Wählen', locale)}</div>
                      {options.map(opt => (
                        <button key={opt} onClick={() => { setTopMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                          <div className="w-4">{topMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                          <span className={topMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
            )}
            {/* Mirrors the w-[52px] stats column so dropdowns align with the columns. */}
            <div className="w-[52px]" />
          </div>
        </div>

        {/* Tournaments */}
        {Object.entries(byTournament).map(([tournament, eventsGroup]) => {
          
          return (
            <React.Fragment key={tournament}>
              {/* Tournament Title Row */}
              <div className="px-5 py-1.5 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center justify-between mt-[-1px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(eventsGroup[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-6 h-[18px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[12px] font-bold text-gray-900">{categoryPrefix}{tournament}</span>
                  <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z" /></svg>
                </div>
                <div className="flex items-center text-[13px] font-bold text-gray-400">
                  {renderMarketColHeader(topMarket1, "w-[151px]")}
                  <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>{renderMarketColHeader(topMarket2, "w-[151px]")}</div>
                  <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>{renderMarketColHeader(topMarket3, "w-[151px]")}</div>
                  {!hideSecondMarket && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(topMarket4, "w-[151px]")}</div>}
                  {!hideSecondMarket && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(topMarket5, "w-[151px]")}</div>}
                  <div className="w-[52px]"></div>
                </div>
              </div>

              {eventsGroup.map((ev) => {
                const total = ev.markets?.length ?? 0;
                const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                const d = starts ? new Date(starts) : null;
                const dateStr = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
                const timeStr = d ? formatTime(d.toISOString()) : '';

                return (
                  <div key={ev.event_id} className="px-5 py-1 flex items-center hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                    
                    <div className="w-[36px] flex-shrink-0 cursor-pointer pt-0.5 text-center items-start justify-start text-left" onClick={() => handleEventClick(ev)}>
                      <div className="text-[13px] font-medium text-gray-500 mb-0.5">{dateStr}</div>
                      <div className="text-[13px] font-medium text-gray-500">{timeStr}</div>
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer border-r border-gray-100 pr-4 pl-2" onClick={() => handleEventClick(ev)}>
                      <div className="flex items-center gap-2 min-w-0">
                        {ev.event?.home?.crest_url ? <img src={ev.event.home.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                        <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event?.home?.name}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        {ev.event?.away?.crest_url ? <img src={ev.event.away.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                        <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event?.away?.name}</span>
                      </div>
                    </div>

                    {/* Dropdown 1 odds */}
                    {renderMarketColCells(ev, topMarket1, "w-[151px]")}
                    
                    {/* Dropdown 2 odds */}
                    <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
                      {renderMarketColCells(ev, topMarket2, "w-[151px]")}
                    </div>
                    <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
                      {renderMarketColCells(ev, topMarket3, "w-[151px]")}
                    </div>
                    {!hideSecondMarket && (
                      <>
                        <div className="hidden min-[1650px]:block flex-shrink-0">
                          {renderMarketColCells(ev, topMarket4, "w-[151px]")}
                        </div>
                        <div className="hidden min-[1750px]:block flex-shrink-0">
                          {renderMarketColCells(ev, topMarket5, "w-[151px]")}
                        </div>
                      </>
                    )}

                    <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-center justify-end text-[15px] font-medium text-gray-500 hover:text-gray-800 transition">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                      {total} ›
                    </button>
                  </div>
                    );
                  })}
            </React.Fragment>
          );
        })}

              </>
        )}

        {activeTab === 'matchkombi' && (
          <>
            {events[0] && (
              <div className="px-5 py-2.5 bg-white flex items-center border-b border-gray-100 min-h-[38px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(events[0].event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-[18px] h-[13px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[14px] font-bold text-gray-900 cursor-default">{categoryPrefix}{events[0].event?.tournament_name}</span>
                </div>
              </div>
            )}
            <div className="p-4">
              {loadingTab ? <div className="p-4 text-center text-gray-500">Wird geladen...</div> : matchKombis.length === 0 ? <div className="p-4 text-center text-gray-500">Keine MatchKombi+ verfügbar.</div> : (
                <div className="grid grid-cols-1 gap-3">
                  {matchKombis.map(mk => (
                    <div key={mk.combination_id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center shadow-sm">
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="text-[14px] font-bold text-gray-900 mb-2 truncate">{mk.event_name}</div>
                        <div className="flex flex-col gap-1">
                          {mk.selections?.map((sel, i) => (
                            <div key={i} className="flex items-center text-[13px] text-gray-700">
                              <span className="w-2 h-2 rounded-full border border-gray-400 mr-2 flex-shrink-0"></span>
                              <span className="truncate"><span className="text-gray-500">{sel.market_name}:</span> <span className="font-bold">{sel.outcome_name}</span></span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        <OddsButton
                          odds={mk.combined_odds}
                          className={`w-[80px] h-[36px] font-bold text-[14px] shadow-sm border rounded-md transition-colors ${
                            isSelected?.(mk)
                              ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]'
                              : 'bg-[#f8f9fa] border-gray-200 text-gray-900 hover:bg-gray-100 dark:bg-[#252525] dark:border-[#333] dark:text-white dark:hover:bg-[#333]'
                          }`}
                          onClick={() => onAddMatchKombi?.(mk)}
                          selected={isSelected?.(mk)}
                        >
                          {formatOdds(mk.combined_odds)}
                        </OddsButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'outrights' && (
          <>
            {events[0] && (
              <div className="px-5 py-2.5 bg-white flex items-center border-b border-gray-100 min-h-[38px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(events[0].event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-[18px] h-[13px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[14px] font-bold text-gray-900 cursor-default">{categoryPrefix}{events[0].event?.tournament_name}</span>
                </div>
              </div>
            )}
            <div className="p-4">
              {loadingTab ? <div className="p-4 text-center text-gray-500">Wird geladen...</div> : outrights.length === 0 ? <div className="p-4 text-center text-gray-500">Keine Ereignisse verfügbar.</div> : (
                <div className="flex flex-col gap-4">
                  {outrights.map(ev => (
                    <div key={ev.event_id} className="bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                        <span className="font-bold text-[14px] text-gray-800">{(ev.event as any).name}</span>
                      </div>
                      <div className="p-3">
                        {ev.markets?.map(m => (
                          <div key={m.market_id} className="mb-4 last:mb-0">
                            <div className="text-[13px] text-gray-500 mb-2 font-medium">{m.name}</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {m.outcomes?.map(o => (
                                <OddsButton
                                  key={o.outcome_id}
                                  odds={o.odds}
                                  className="w-full flex flex-row items-center justify-between bg-[#f4f5f6] border border-gray-200 rounded-md text-gray-900 hover:bg-gray-200 dark:bg-[#1a1a1a] dark:text-white dark:border-[#444] dark:hover:bg-[#2a2a2a] transition px-3 py-2"
                                  onClick={() => {}}
                                >
                                  <span className="text-[12px] font-medium text-gray-700 dark:text-gray-300 line-clamp-1 text-left flex-1 pr-2">{o.name}</span>
                                  <span className="text-[14px] font-bold flex-shrink-0">{formatOdds(o.odds)}</span>
                                </OddsButton>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
